/**
 * THE TV(s) THE DESKTOP CASTS TO, as this page sees them: the pushed TV — or, in "Broadcast mode (older TVs)",
 * the TVs pulling the cast — with their name and OFFER (cast/offer.ts — the worlds they can't show, and those they
 * offer beyond the catalogue). The Rust
 * side owns both (tvcast.rs: the push offer is part of the route's status, so a route change clears it and a
 * superseded connection can't set it; audio.rs: each pulling TV's entry lives exactly as long as its
 * connection); this is the page's one mirror of it, refreshed from every status read (cast/sender.ts tvcastStatus). While casting, the Director's pool leaves
 * these worlds out, the picker marks them "Not available on <TV>", and picking one says so — the TV keeps
 * its current visual (receiver/mirror.ts), this computer still plays it. The lite worlds it offers (`extra`) are
 * what this computer may list (catalogue.setSenderLite) — set HERE, as the offer is adopted, whichever view is on
 * screen: a view only reads the catalogue, it never keeps eligibility for it.
 */
import type { OfferReason } from './offer'
import { setSenderLite } from '../catalogue'

export interface TvOfferLists { heavy: string[]; unreachable: string[]; unsupported: string[]; extra?: string[] }

let tvName: string | null = null
let unavailable = new Map<string, OfferReason>()
let key = ''
const listeners = new Set<() => void>()

/** Adopt the route's current TV + offer (null name = not casting). Fires the listeners only on a change. */
export function setCastTarget(name: string | null, offer: TvOfferLists | null): void {
  const next = new Map<string, OfferReason>()
  if (name && offer) {
    for (const reason of ['heavy', 'unreachable', 'unsupported'] as const) {
      for (const id of offer[reason] ?? []) if (!next.has(id)) next.set(id, reason)
    }
  }
  const nextExtra = name && offer ? [...new Set(offer.extra ?? [])] : []
  const nextKey = name ? `${name}|${[...next].map(([id, r]) => `${id}:${r}`).join(',')}|+${nextExtra.join(',')}` : ''
  if (nextKey === key) return
  key = nextKey
  tvName = name
  unavailable = next
  setSenderLite(nextExtra)
  for (const cb of [...listeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
}

/** The TV this computer is casting to, or null. */
export function castTargetName(): string | null {
  return tvName
}

/** Why the TV this computer casts to can't show a world — or null (available there, or not casting). */
export function castTargetUnavailable(id: string): OfferReason | null {
  return unavailable.get(id) ?? null
}

/** Subscribe to the cast target's changes (a new TV, a new offer, the cast ending). */
export function onCastTargetChange(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}
